import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Button, ButtonProps } from 'primereact/button'
import { confirmDialog } from 'primereact/confirmdialog'

import { useToastContext } from '../../contexts/toast'
import { getHandleableError } from '../../utils/errors/isXiaError'

type Props = {
  fileName: string
  attachmentId: string
  attachmentUri: string
  onDelete: (id: string, uri: string) => void
} & ButtonProps

export const AttachmentDeleteButton: React.FC<Props> = ({
  fileName,
  attachmentId,
  attachmentUri,
  onDelete,
  ...props
}) => {
  const { t } = useTranslation('attachment')
  const { replaceToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  const handleDelete = async () => {
    setIsRequestRunning(true)
    try {
      await onDelete(attachmentId, attachmentUri)

      replaceToast({
        style: {},
        closable: true,
        severity: 'success',
        summary: t('delete.confirm.accept.messages.success.summary'),
        detail: t('delete.confirm.accept.messages.success.detail', {
          fileName,
        }),
      })
    } catch (error) {
      let handleableError = getHandleableError(error)
      let errorMessage = handleableError.message

      replaceToast({
        style: {},
        closable: true,
        severity: 'error',
        summary: t('delete.confirm.accept.messages.error.summary'),
        detail: errorMessage,
      })
    } finally {
      setIsRequestRunning(false)
    }
  }

  const handleOnClick = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
  ) => {
    confirmDialog({
      message: t('delete.confirm.message', {
        fileName,
      }),
      header: t('delete.confirm.header'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: t('delete.confirm.accept.label'),
      rejectLabel: t('delete.confirm.reject.label'),
      accept: handleDelete,
    })
  }

  return (
    <Button
      icon="pi pi-trash"
      rounded
      text
      severity="danger"
      aria-label={t('delete.label')}
      onClick={handleOnClick}
      loading={isRequestRunning}
      tooltipOptions={{ position: 'top' }}
      {...props}
    />
  )
}
